/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/naming-convention */
// types
import {
    ApiMenuCartItem,
    CartItem,
    GiftingLocalCart,
    CART_ERROR_CTA,
    MROChildCart,
    MROChildCartState,
    MROChildLocalCart,
    MRONativeLocalCart,
    ReviewedCart,
    MenuItemsCalloutInfo,
    NativeLocalCart,
    ReviewedCartApiResponse,
} from "@swiggy-private/cart-types";
import { GiftingReviewedCart } from "@cart-client/interfaces/GiftingCart";
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_CATEGORY, MARKETPLACE_ID } from "@swiggy-private/cart-types";
import { CART_STATUS_CODES, isItemAvailable } from "@swiggy-private/cart-ui";
import {
    recordNREvent,
    NEWRELIC_TABLES,
    isMROFlow as isMROFlowByHeader,
    getBridgeSdk,
    getMroFlowNRAttrs,
    isQuickCheckoutFlow,
} from "@swiggy-private/checkout-core";
import { MROReviewedCart, MROUpdateCartData } from "@cart-client/interfaces/MROFoodCart";
import { GlobalHeaderState, SwgyHeaderKeysList } from "@swiggy-private/checkout-core";
import { INTERNAL_CART_SOURCE } from "@cart-client/interfaces/Cart";
import { DEFAULT_MRO_CART_HEADER, EVENT_SRC_TYPE } from "@cart-client/constants/MROConstants";

// utils
import { getNestedValue, isEmpty } from "@swiggy-private/js-utils";

// constants
import { CART_ITEM_REWARD_TYPE } from "../constants";

export const normaliseId = (id?: string | number | null): string => {
    return String(id ?? "");
};

export const getMROCartType = () => {
    return (GlobalHeaderState.getHeader(SwgyHeaderKeysList.xCartType) || "").toLowerCase();
};

/**
 * Cart-layer MRO check: header-based MRO/AIP flow, optionally gated by cartSource
 * (e.g. exclude gifting overlays that share the same session headers).
 */
export const isMROFlow = (cartSource?: INTERNAL_CART_SOURCE): boolean => {
    const approvedFlowList = [INTERNAL_CART_SOURCE.MRO_FOOD, INTERNAL_CART_SOURCE.QUICK_CHECKOUT];
    const isValidCartSource = isEmpty(cartSource) || approvedFlowList.includes(cartSource);
    return isMROFlowByHeader() && isValidCartSource;
};

export const isValidMROCart = (cartResponse?: MROReviewedCart | null): boolean => {
    return isMROFlow() && !isEmptyMROCart(cartResponse?.data);
};

export const isEmptyMROCart = (data?: MROReviewedCart["data"] | MRONativeLocalCart | null): boolean => {
    return isEmpty(data?.child_carts);
};

export const getMROChildCarts = (cartResponse?: MROReviewedCart | null): MROChildCart[] => {
    return cartResponse?.data?.child_carts ?? [];
};

export const getMROChildCartIds = (cartResponse?: MROReviewedCart | null): number[] => {
    return getMROChildCarts(cartResponse).map((childCart) => childCart.cartId);
};

/**
 * Restaurant ids in the order the server returned the child carts in the last
 * update-cart response. apply-coupon / remove-coupon do not send `child_carts`,
 * so the server needs this list to keep the same child cart render order.
 */
export const getMROCartRxRenderOrder = (cartResponse?: MROReviewedCart | null): string[] => {
    return getMROChildCarts(cartResponse)
        .map((childCart) => getMROChildCartRestaurantId(childCart))
        .filter((restaurantId) => !isEmpty(restaurantId));
};

export const getMROChildCartByRestaurantId = (
    cartResponse?: MROReviewedCart | null,
    restaurantId?: string | number,
): MROChildCart | undefined => {
    if (isEmpty(restaurantId)) {
        return undefined;
    }
    const target = normaliseId(restaurantId);
    return getMROChildCarts(cartResponse).find((child) => getMROChildCartRestaurantId(child) === target);
};

export const getMROChildCartId = (child?: MROChildCart | MROChildLocalCart): string => {
    return normaliseId(child?.cartId);
};

/**
 * Reads the cart id off a local cart regardless of flow.
 * BAU local carts populate `cart_id` (snake_case, see Cart.ts); MRO local/child carts
 * only ever populate `cartId` (camelCase, see MROFoodCartPayloadPrep.ts).
 */
export const getLocalCartId = (
    localCart?: NativeLocalCart | MRONativeLocalCart | MROChildLocalCart | null,
): number | undefined => {
    return (localCart as NativeLocalCart)?.cart_id ?? (localCart as MRONativeLocalCart)?.cartId;
};

export const getMROChildCartRestaurantId = (child?: MROChildCart | MROChildLocalCart): string => {
    return normaliseId((child as MROChildCart)?.restaurant_details?.id || (child as MROChildLocalCart)?.restaurantId);
};

export const getMROChildCartRestaurantName = (child?: MROChildCart | MROChildLocalCart): string => {
    return (child as MROChildCart)?.restaurant_details?.name || (child as MROChildLocalCart)?.restaurantName || "";
};

export const getMROSelectedChildCart = (
    cartResponse?: MROReviewedCart | null,
    selectedRestaurantId?: string | number,
): MROChildCart | undefined => {
    const childCarts = getMROChildCarts(cartResponse);
    if (isEmpty(childCarts)) {
        return undefined;
    }
    const explicit = getMROChildCartByRestaurantId(cartResponse, selectedRestaurantId);
    if (!isEmpty(explicit)) {
        return explicit;
    }
    const selectedByState = childCarts.find((child) => child.cart_state === MROChildCartState.SELECTED);
    return selectedByState || undefined;
};

export const getMROChildCartItemsCount = (child?: MROChildCart): number => {
    return (child?.cart_menu_items ?? []).reduce((count: number, item: CartItem) => count + (item?.quantity ?? 0), 0);
};

export const getMROLocalCartItemsCount = (child?: MROChildLocalCart): number => {
    return (child?.cartItems ?? []).reduce((count: number, item: ApiMenuCartItem) => count + (item?.quantity ?? 0), 0);
};

export const getMROTotalItemsCount = (cartResponse?: MROReviewedCart | null): number => {
    return getMROChildCarts(cartResponse).reduce((total, child) => total + getMROChildCartItemsCount(child), 0);
};

/**
 * Whole-cart error resolution. Parent `statusCode` is the source of truth; this
 * is a thin helper so the renderer does not duplicate the check. BE is expected
 * to pre-resolve the winning child error into the parent status (see contract
 * review) — the client should not rank child errors itself.
 */
export const isMROCartStatusSuccess = (cartResponse?: MROReviewedCart | null): boolean => {
    return cartResponse?.statusCode === CART_STATUS_CODES.SUCCESS;
};

/** Check if the child cart status is success for Restaurant Card Errors */
export const isMROChildCartStatusSuccess = (child?: MROChildCart): boolean => {
    return child?.statusCode === CART_STATUS_CODES.SUCCESS;
};

/** used to get the restaurant details for the most recently created child cart
 * needed for fetching the addresses for the most recently created child cart in MRO flows
 */
export const getRestaurantDetailsForMostRecentlyCreatedMROChildCart = (
    cartResponse?: MROReviewedCart | null,
): MROChildCart["restaurant_details"] | undefined => {
    const clonedChildCarts = [...(cartResponse?.data?.child_carts || [])];
    const mostRecentlyCreatedCart = clonedChildCarts.sort((a: MROChildCart, b: MROChildCart) => {
        if (isEmpty(a.created_at) || isEmpty(b.created_at)) {
            return -1;
        }
        return Number(b.created_at) - Number(a.created_at);
    });
    return mostRecentlyCreatedCart?.[0]?.restaurant_details;
};

/** used to build the cart header with the restaurant name for MRO flows */
export const buildMROCartHeaderWithRXName = (cartResponse?: MROReviewedCart | null): string => {
    const childCarts = cartResponse?.data?.child_carts || [];
    if (childCarts.length === 1) {
        return getMROChildCartRestaurantName(childCarts[0]) || DEFAULT_MRO_CART_HEADER;
    }
    return DEFAULT_MRO_CART_HEADER;
};

/** used to build the list identifier for the MRO cart, needed for places where we are doing capping based on RxID */
export const buildRXListIdentifierForMROCart = (cartResponse?: MROReviewedCart | null): string => {
    // merge all the restaurant ids from the child carts
    const restaurantIds =
        cartResponse?.data?.child_carts?.map((child: MROChildCart) => getMROChildCartRestaurantId(child)) ?? [];
    return restaurantIds.join("_"); // _ is used to separate the restaurant ids
};

export const isRXPresentInSelectedList = (selectedRXIds: string[], restaurantId: string): boolean => {
    return selectedRXIds.includes(normaliseId(restaurantId));
};

export const getMROChildCartFromApplyCouponWidgetInfo = (
    cartResponse?: MROReviewedCart,
    applyCouponWidgetInfo?: MROChildCart["apply_coupon_widget_info"],
): MROChildCart | undefined => {
    if (isEmpty(applyCouponWidgetInfo?.mroChildCartRestaurantId)) {
        return undefined;
    }
    const restaurantId = normaliseId(applyCouponWidgetInfo?.mroChildCartRestaurantId);
    return getMROChildCartByRestaurantId(cartResponse, restaurantId);
};

export const validateAllSubcartsSelectedInMROCart = (
    cartResponse?: MROReviewedCart,
    localCart?: MRONativeLocalCart | null,
): boolean => {
    if (isEmpty(localCart) || isEmpty(cartResponse)) {
        return false;
    }

    // validate if the selectedRXId, localCarts and serverCarts are the same
    const selectedRXId = localCart?.selected_rxids || [];
    const childCarts = getMROChildCarts(cartResponse) || [];
    const localChildCart = localCart?.child_carts || [];

    // dedupe the selectedRXId, localCarts and serverCarts
    const selectedRXIdSet = new Set(selectedRXId);
    const localChildCartSet = new Set(
        localChildCart.map((child: MROChildLocalCart) => getMROChildCartRestaurantId(child)),
    );
    const serverChildCartSet = new Set(childCarts.map((child) => getMROChildCartRestaurantId(child)));

    // validate if the selectedRXId, localCarts and serverCarts are the same
    return selectedRXIdSet.size === serverChildCartSet.size && localChildCartSet.size === serverChildCartSet.size;
};

export const logMROEventsIntoNR = (payload: Record<string, unknown> = {}) => {
    recordNREvent({
        eventType: NEWRELIC_TABLES.MRO_FOOD_CART,
        ...getMroFlowNRAttrs(),
        ...payload,
    });
};

export const hasMROChildCartSplashLottie = (cartResponse?: MROReviewedCart | null): boolean => {
    // only single RX MRO carts are supported for splash lottie
    return !isEmpty(cartResponse?.data?.cart_splash_lottie);
};

export const getMROChildCartFromLocalCartByRxId = (
    localCart?: MRONativeLocalCart | null,
    restaurantId?: string | number,
): MROChildLocalCart | undefined => {
    if (isEmpty(localCart) || isEmpty(restaurantId)) {
        return undefined;
    }
    return localCart?.child_carts?.find(
        (child: MROChildLocalCart) => getMROChildCartRestaurantId(child) === normaliseId(restaurantId),
    );
};

/**
 * True when at least one item in the list is a real (non-steal-deal) item.
 * A cart/list holding only steal-deal items should be treated as empty by
 * callers deciding whether a child cart/update still represents a real order.
 */
export const hasNonStealDealItem = (items?: Array<ApiMenuCartItem | CartItem> | null): boolean =>
    (items || []).some((item) => item.rewardType !== CART_ITEM_REWARD_TYPE.SLASH_DEAL);

/**
 * Picks the first remaining child cart to auto-select after a delete leaves no tab selected.
 * Skips a child cart that isEmptyCartWithStealDealCheck (called downstream in updateCartPublic)
 * would itself treat as invalid — empty, or holding only steal-deal items. Selecting one of
 * those would make the filtered payload look empty and flip the call into a flush-cart,
 * silently dropping any other sibling cart (e.g. an unselected one with real items) that never
 * got sent.
 */
export const findChildCartToAutoSelect = (childCarts: MROChildLocalCart[]): MROChildLocalCart | undefined =>
    childCarts.find((child) => !isEmpty(child.mealItems) || hasNonStealDealItem(child.cartItems));

/**
 * When a child-cart delete leaves no tab selected, auto-selects the first remaining
 * eligible child cart so the update-cart payload never carries zero selected carts —
 * the backend would otherwise echo back an empty cart and bounce the user off the
 * cart page. Selection is derived fresh from the child_carts about to be sent, not
 * from the incoming selected_rxids. Returns updateCartData unchanged when no
 * child-cart delete happened, a tab is already selected, or no eligible cart remains.
 */
export const autoSelectChildCartAfterDelete = (updateCartData: MROUpdateCartData): MROUpdateCartData => {
    const childCarts = updateCartData.child_carts;
    const hasSelectedChildCart = childCarts?.some((child) => child.cart_state === MROChildCartState.SELECTED);

    // hasunselected cart and !quickCheckout
    if (isEmpty(childCarts) || hasSelectedChildCart || isQuickCheckoutFlow()) {
        return updateCartData;
    }

    const childCartToAutoSelect = findChildCartToAutoSelect(childCarts as MROChildLocalCart[]);
    if (!childCartToAutoSelect) {
        return updateCartData;
    }

    return {
        ...updateCartData,
        child_carts: (childCarts as MROChildLocalCart[]).map((child) =>
            child === childCartToAutoSelect ? { ...child, cart_state: MROChildCartState.SELECTED } : child,
        ),
        selected_rxids: [normaliseId(getMROChildCartRestaurantId(childCartToAutoSelect))],
    };
};

/**
 * Returns the effective server cart data for a given context:
 * - MRO: finds the matching MROChildCart by restaurantId from the parent reviewed cart
 * - BAU: returns cartResponse.data directly
 */
export const effectiveServerCartComputer = (
    cartResponse: MROReviewedCart | ReviewedCart | GiftingReviewedCart | null | undefined,
    isMRO: boolean,
    restaurantId?: string | number,
): MROChildCart | ReviewedCartApiResponse | undefined => {
    if (isMRO && !isEmpty(restaurantId)) {
        return getMROChildCartByRestaurantId(cartResponse as MROReviewedCart, restaurantId);
    }
    return cartResponse?.data ?? undefined;
};

/**
 * Returns the effective local cart for a given context:
 * - MRO: finds the matching MROChildLocalCart by restaurantId from the parent local cart
 * - BAU: returns localCart directly
 */
export const effectiveLocalCartComputer = (
    localCart: MRONativeLocalCart | null | undefined,
    isMRO: boolean,
    restaurantId?: string | number,
): NativeLocalCart | MRONativeLocalCart | null | undefined => {
    if (isMRO && !isEmpty(restaurantId)) {
        return getMROChildCartFromLocalCartByRxId(localCart, restaurantId);
    }
    return localCart;
};

/**
 * Returns the effective cart data for delivery speed / widget consumption:
 * - MRO: cartResponse is already the MROChildCart, return as-is
 * - BAU: return cartResponse.data
 */
export const getEffectiveCartData = (
    cartResponse: ReviewedCart | MROChildCart | null,
    isMRO: boolean,
): MROChildCart | ReviewedCart["data"] | undefined => {
    return isMRO ? (cartResponse as MROChildCart) : ((cartResponse as ReviewedCart)?.data ?? undefined);
};

export const buildOrderCommentPayload = (
    localCart: MRONativeLocalCart | NativeLocalCart | null | undefined,
    isMRO: boolean,
    restaurantId: string | number | undefined,
    note: string,
): MRONativeLocalCart | NativeLocalCart => {
    if (isMRO) {
        const mroLocalCart = localCart as MRONativeLocalCart;
        return {
            ...mroLocalCart,
            child_carts: mroLocalCart?.child_carts?.map((child: MROChildLocalCart) =>
                getMROChildCartRestaurantId(child) === normaliseId(restaurantId)
                    ? { ...child, orderComment: note }
                    : child,
            ),
        };
    }
    return { ...localCart, orderComment: note } as NativeLocalCart;
};

/**
 * Builds the local-cart payload with `cart_saved_preferences` patched in.
 * MRO: patches only the matching child cart by restaurantId.
 * BAU: patches the top-level cart.
 */
export const buildCutleryUpdatePayload = (
    localCart: MRONativeLocalCart | NativeLocalCart | null | undefined,
    isMRO: boolean,
    restaurantId: string | number | undefined,
    isOptedValue: string,
): MRONativeLocalCart | NativeLocalCart => {
    if (isMRO && !isEmpty(restaurantId)) {
        const mroLocalCart = localCart as MRONativeLocalCart;
        const updatedChildCarts = mroLocalCart?.child_carts?.map((child: MROChildLocalCart) =>
            getMROChildCartRestaurantId(child) === normaliseId(restaurantId)
                ? {
                      ...child,
                      cart_saved_preferences: {
                          ...(child.cart_saved_preferences ?? {}),
                          is_cutlery_opted: isOptedValue,
                      },
                  }
                : child,
        );
        return { ...mroLocalCart, child_carts: updatedChildCarts };
    }
    return {
        ...localCart,
        cart_saved_preferences: {
            ...((localCart as NativeLocalCart)?.cart_saved_preferences ?? {}),
            is_cutlery_opted: isOptedValue,
        },
    } as NativeLocalCart;
};

export const filterLocalAndServerCartFromPayload = ({
    isMROCart,
    localCartState,
    reviewCartState,
    restaurantId,
}: {
    isMROCart?: boolean;
    localCartState?: NativeLocalCart | GiftingLocalCart | MRONativeLocalCart | null;
    reviewCartState?: MROReviewedCart | ReviewedCart | GiftingReviewedCart | null;
    restaurantId?: string | number;
}) => {
    if (isMROCart && !isEmpty(restaurantId)) {
        const localCartForRestaurant: MROChildLocalCart | undefined = getMROChildCartFromLocalCartByRxId(
            localCartState as MRONativeLocalCart,
            restaurantId,
        );
        const reviewedCartForRestaurant: MROChildCart | undefined = getMROChildCartByRestaurantId(
            reviewCartState as MROReviewedCart,
            restaurantId,
        );
        return {
            localCart: localCartForRestaurant as unknown as NativeLocalCart,
            reviewedCart: { data: reviewedCartForRestaurant } as unknown as ReviewedCart,
        };
    }
    return {
        localCart: localCartState as NativeLocalCart,
        reviewedCart: reviewCartState as ReviewedCart,
    };
};

export const clearMROLocalCart = (): void => {
    getBridgeSdk().clearLocalCart({
        tenant: {
            marketplaceBusinessline: MARKETPLACE_BUSINESS_LINE.REGULAR,
            marketplaceCategory: MARKETPLACE_CATEGORY.FOOD,
            marketplaceId: MARKETPLACE_ID.SWIGGY,
            cartType: getMROCartType(),
        },
    });
};

export const stripUnavailableItemsFromChildCart = (
    localChildCarts: MROChildLocalCart[],
    errorRestaurantId: string,
    reviewedChildCart: MROChildCart | undefined,
): MROChildLocalCart[] => {
    const availableItemIds = new Set(
        (reviewedChildCart?.cart_menu_items ?? [])
            .filter((item) => isItemAvailable(item))
            .map((item) => item.menu_item_id),
    );
    return localChildCarts.reduce<MROChildLocalCart[]>((acc, localChildCart) => {
        if (normaliseId(getMROChildCartRestaurantId(localChildCart)) !== errorRestaurantId) {
            acc.push(localChildCart);
            return acc;
        }
        const remainingItems = (localChildCart.cartItems ?? []).filter((i) => availableItemIds.has(i.menu_item_id));
        // Dropping the unavailable item can leave only a steal-deal item behind — that isn't
        // a real order any more, so treat it the same as an empty child cart (matches the
        // check findChildCartToAutoSelect/getItemsListForCartUpdate already apply elsewhere).
        if (remainingItems.length > 0 && hasNonStealDealItem(remainingItems)) {
            acc.push({ ...localChildCart, cartItems: remainingItems });
        }
        return acc;
    }, []);
};

export const computeUpdatedChildCartsForErrorRemoval = (
    localChildCarts: MROChildLocalCart[],
    reviewedSelectedChildren: MROChildCart[],
    errorChildren: MROChildCart[],
    reloadCart: () => void,
): MROChildLocalCart[] => {
    let updatedChildCarts = localChildCarts;

    for (const errorChild of errorChildren) {
        const errorRestaurantId = getMROChildCartRestaurantId(errorChild);
        const errorChildCtaAction = errorChild?.ctaAction?.toLowerCase();

        switch (errorChildCtaAction) {
            case CART_ERROR_CTA.CLEAR_CART:
                updatedChildCarts = updatedChildCarts.filter(
                    (child_cart) => getMROChildCartRestaurantId(child_cart) !== errorRestaurantId,
                );
                break;
            case CART_ERROR_CTA.REMOVE_UNAVAILABLE_ITEMS: {
                const reviewedChildCart = reviewedSelectedChildren.find(
                    (child_cart) => getMROChildCartRestaurantId(child_cart) === errorRestaurantId,
                );
                updatedChildCarts = stripUnavailableItemsFromChildCart(
                    updatedChildCarts,
                    errorRestaurantId,
                    reviewedChildCart,
                );
                break;
            }
            default: {
                if (errorChildren.length === 1) {
                    reloadCart?.();
                } else {
                    // unknown error in child
                    logMROEventsIntoNR({
                        type: EVENT_SRC_TYPE.MRO_CHILD_CTA_UNKNOWN_ACTION,
                        rxId: errorRestaurantId,
                        ctaAction: errorChild?.ctaAction,
                    });
                }
                break;
            }
        }
    }

    return updatedChildCarts;
};

export const getMroMenuCalloutInfo = (
    cartResponse: MROChildCart | null,
): { isAvailable: boolean; info?: MenuItemsCalloutInfo } => {
    const info = getNestedValue(cartResponse, "presentation_attributes_keys.menu_items_callout") as
        | MenuItemsCalloutInfo
        | undefined;

    return {
        isAvailable: !isEmpty(cartResponse?.presentation_attributes_keys?.menu_items_callout?.message),
        info: !isEmpty(info) ? info : undefined,
    };
};

export const getMROChildRxCalloutImpressionPayload = (
    cartResponse: MROReviewedCart | null,
    effectiveCartData?: MROChildCart,
): { calloutText?: string; parentCartId?: number | string; originalCartId?: number | string } => ({
    calloutText:
        getMroMenuCalloutInfo(effectiveCartData as MROChildCart)?.info?.message ||
        effectiveCartData?.self_delivery_presentation_info?.title,
    parentCartId: cartResponse?.data?.cartId,
    originalCartId: effectiveCartData?.originalCartId,
});

export const wrapMROChildCartAsReviewCartDataObject = (
    data: MROChildCart | ReviewedCart["data"] | undefined,
): ReviewedCart => {
    return { data } as ReviewedCart;
};

export const sendNRLogsForFooterCtaAction = (cartResponse: MROReviewedCart | null, ctaActionType: string) => {
    const childCarts = getMROChildCarts(cartResponse);
    const ctaActionList: Record<string, string> = {};
    const ctaStatusCodeList: Record<string, number> = {};

    childCarts.forEach((child_cart: MROChildCart) => {
        const rxId = getMROChildCartRestaurantId(child_cart);
        ctaActionList[rxId] = child_cart.ctaAction ?? "";
        ctaStatusCodeList[rxId] = child_cart.statusCode ?? 0;
    });

    logMROEventsIntoNR({
        ctaActionType,
        type: EVENT_SRC_TYPE.MRO_FOOTER_CTA_ACTION,
        ctaActionList: ctaActionList,
        ctaStatusCodeList: ctaStatusCodeList,
    });
};

export const updateCartDataWithSelectedRxIds = (
    updateCartData: MROUpdateCartData,
    updateCartResponse: MROReviewedCart,
) => {
    return {
        ...updateCartData,
        selected_rxids:
            updateCartResponse.data?.child_carts?.map((childCart: MROChildCart) =>
                getMROChildCartRestaurantId(childCart),
            ) ?? [],
    };
};
